import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { toast } from "sonner";
import { hotelPartnerCurationService } from "@/services/hotel-partner-curation-service";
import { describeError } from "@/components/pages/admin-partners/utils/describeError";
import { getErrorMessage, getHttpStatus } from "@/utils/httpErrors";
import { useDebounce } from "@/hooks/useDebounce";
import type {
  PartnerHotelCatalogItem,
  PartnerHotelCatalogResponse,
  PartnerHotelCuration,
  PartnerHotelCurationItem,
  UpdatePartnerHotelCurationOverridesRequest,
} from "@/types/partner-destination";

export const hotelPartnerCurationKeys = {
  all: ["hotelPartnerCuration"] as const,
  curation: (hotelId: string) =>
    [...hotelPartnerCurationKeys.all, "curation", hotelId] as const,
  catalog: (hotelId: string, search: string) =>
    [...hotelPartnerCurationKeys.all, "catalog", hotelId, search] as const,
};

export const useHotelCuration = (hotelId: string | undefined) => {
  return useQuery<PartnerHotelCuration>({
    queryKey: hotelPartnerCurationKeys.curation(hotelId || ""),
    enabled: !!hotelId,
    queryFn: () => hotelPartnerCurationService.getCuration(hotelId!),
    staleTime: 30 * 1000,
  });
};

/**
 * A 400 here is always one of three known preconditions (a code outside the
 * city's catalog, adding a code already in the list, or the same code in
 * two of the four arrays) — picked by matching the extracted, already-
 * normalized error message (`getErrorMessage`, never the raw response body)
 * against each case's own wording, so the screen always shows a fixed
 * translated sentence rather than anything passed through from the backend.
 * Falls back to the generic save-error text for anything else (network
 * error, 500, an unrecognized 400).
 */
function describeHotelCurationSaveError(t: TFunction, error: unknown): string {
  if (getHttpStatus(error) === 400) {
    const message = getErrorMessage(error)?.toLowerCase() ?? "";
    if (message.includes("catalog")) {
      return t("partners.curation.saveErrorNotInCatalog");
    }
    if (message.includes("already")) {
      return t("partners.curation.saveErrorAlreadyInList");
    }
    if (message.includes("both") || message.includes("conflict")) {
      return t("partners.curation.saveErrorConflictingChange");
    }
  }
  return describeError(t("partners.curation.saveError"), error);
}

export const useUpdateHotelCurationOverrides = (
  hotelId: string | undefined,
) => {
  const { t } = useTranslation();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdatePartnerHotelCurationOverridesRequest) => {
      if (!hotelId) throw new Error("hotelId is required");
      return hotelPartnerCurationService.updateCurationOverrides(hotelId, data);
    },
    onSuccess: () => {
      if (!hotelId) return;
      qc.invalidateQueries({
        queryKey: hotelPartnerCurationKeys.curation(hotelId),
      });
    },
    onError: (error) => {
      toast.error(describeHotelCurationSaveError(t, error));
    },
  });
};

const CATALOG_PAGE_LIMIT = 20;
const CATALOG_SEARCH_DEBOUNCE_MS = 300;

/**
 * "Add from Viator" dialog search. Unlike `GuesteriaPartnerEditRegionDialog`
 * (which requires a typed query), this browses the city's catalog by
 * default — an empty search still fetches page 1 — since there's no
 * "type to search" copy in the contract for this dialog and the brief
 * frames it as browsing "the city's Viator catalog", not a lookup.
 */
export const useHotelPartnerCatalogSearch = (
  hotelId: string | undefined,
  searchInput: string,
  enabled: boolean,
) => {
  const debouncedSearch = useDebounce(searchInput, CATALOG_SEARCH_DEBOUNCE_MS);
  const trimmedSearch = debouncedSearch.trim();

  const query = useQuery<PartnerHotelCatalogResponse>({
    queryKey: hotelPartnerCurationKeys.catalog(hotelId || "", trimmedSearch),
    enabled: enabled && !!hotelId,
    queryFn: () =>
      hotelPartnerCurationService.getCatalog(hotelId!, {
        search: trimmedSearch || undefined,
        page: 1,
        limit: CATALOG_PAGE_LIMIT,
      }),
    staleTime: 10 * 1000,
  });

  return {
    ...query,
    isDebouncing: searchInput.trim() !== trimmedSearch,
  };
};

function catalogItemToPendingCurationItem(
  item: PartnerHotelCatalogItem,
): PartnerHotelCurationItem {
  return {
    productCode: item.productCode,
    title: item.title,
    imageUrl: item.imageUrl,
    rating: item.rating,
    reviewCount: item.reviewCount,
    price: item.price,
    currency: item.currency,
    // The catalog item doesn't carry kind/active (not in the catalog
    // contract) — "product"/true is the local preview until the overrides
    // PUT materializes and the next curation fetch returns the real values.
    kind: "product",
    active: true,
    // "hotel" — this codebase's dedicated source value for hotel-added
    // items (distinct from the admin side's ai_top/ai_score/manual).
    source: "hotel",
    hidden: false,
    position: -1,
  };
}

/**
 * Local, unsaved diff of hide/unhide/add/remove changes against the last
 * loaded `PartnerHotelCuration`. Nothing here fires a network call — the
 * page's single Save button turns the diff into one
 * `UpdatePartnerHotelCurationOverridesRequest` PUT.
 */
export const useCurationDraft = (
  curation: PartnerHotelCuration | undefined,
) => {
  const [hiddenOverrides, setHiddenOverrides] = useState<Map<string, boolean>>(
    new Map(),
  );
  const [stagedAdds, setStagedAdds] = useState<
    Map<string, PartnerHotelCurationItem>
  >(new Map());
  const [stagedRemoves, setStagedRemoves] = useState<Set<string>>(new Set());

  const items = useMemo(() => curation?.items ?? [], [curation]);
  const added = useMemo(() => curation?.added ?? [], [curation]);

  const effectiveItems = useMemo(
    () =>
      items.map((item) => ({
        ...item,
        hidden: hiddenOverrides.get(item.productCode) ?? item.hidden,
      })),
    [items, hiddenOverrides],
  );

  const effectiveAdded = useMemo(() => {
    const kept = added
      .filter((item) => !stagedRemoves.has(item.productCode))
      .map((item) => ({ ...item, pending: false as const }));
    const pending = Array.from(stagedAdds.values()).map((item) => ({
      ...item,
      pending: true as const,
    }));
    return [...kept, ...pending];
  }, [added, stagedRemoves, stagedAdds]);

  // Every product code already on the city list or already added by this
  // hotel (staged or saved) — the add-dialog uses this to show "Added"
  // instead of an Add button.
  const addedOrCityCodes = useMemo(() => {
    const codes = new Set<string>();
    items.forEach((item) => codes.add(item.productCode));
    added.forEach((item) => {
      if (!stagedRemoves.has(item.productCode)) codes.add(item.productCode);
    });
    stagedAdds.forEach((_item, code) => codes.add(code));
    return codes;
  }, [items, added, stagedRemoves, stagedAdds]);

  const toggleHidden = useCallback(
    (productCode: string, nextHidden: boolean) => {
      const original =
        items.find((item) => item.productCode === productCode)?.hidden ?? false;
      setHiddenOverrides((prev) => {
        const next = new Map(prev);
        if (nextHidden === original) {
          next.delete(productCode);
        } else {
          next.set(productCode, nextHidden);
        }
        return next;
      });
    },
    [items],
  );

  const stageAdd = useCallback(
    (item: PartnerHotelCatalogItem) => {
      if (stagedRemoves.has(item.productCode)) {
        // It was a saved "added" item staged for removal in this session —
        // adding it again just cancels that staged removal.
        setStagedRemoves((prev) => {
          const next = new Set(prev);
          next.delete(item.productCode);
          return next;
        });
        return;
      }
      setStagedAdds((prev) => {
        if (prev.has(item.productCode)) return prev;
        const next = new Map(prev);
        next.set(item.productCode, catalogItemToPendingCurationItem(item));
        return next;
      });
    },
    [stagedRemoves],
  );

  const stageRemove = useCallback(
    (productCode: string) => {
      if (stagedAdds.has(productCode)) {
        // Staged-but-unsaved add in this same session — undo it locally
        // instead of asking the backend to remove something it never saw.
        setStagedAdds((prev) => {
          const next = new Map(prev);
          next.delete(productCode);
          return next;
        });
        return;
      }
      setStagedRemoves((prev) => {
        if (prev.has(productCode)) return prev;
        const next = new Set(prev);
        next.add(productCode);
        return next;
      });
    },
    [stagedAdds],
  );

  const hasChanges =
    hiddenOverrides.size > 0 || stagedAdds.size > 0 || stagedRemoves.size > 0;

  const reset = useCallback(() => {
    setHiddenOverrides(new Map());
    setStagedAdds(new Map());
    setStagedRemoves(new Set());
  }, []);

  const buildRequest =
    useCallback((): UpdatePartnerHotelCurationOverridesRequest => {
      const hide: string[] = [];
      const unhide: string[] = [];
      hiddenOverrides.forEach((hidden, productCode) => {
        if (hidden) {
          hide.push(productCode);
        } else {
          unhide.push(productCode);
        }
      });
      return {
        hide,
        unhide,
        add: Array.from(stagedAdds.keys()),
        remove: Array.from(stagedRemoves),
      };
    }, [hiddenOverrides, stagedAdds, stagedRemoves]);

  return {
    effectiveItems,
    effectiveAdded,
    addedOrCityCodes,
    toggleHidden,
    stageAdd,
    stageRemove,
    hasChanges,
    reset,
    buildRequest,
  };
};
